import { NODE_RANK } from "@components/nav/DagDataLibrary.component";
import { getGroupValueForNodeType } from "@services/modeling.service";
import { invalidateEverything, invalidatePipelineNodes, MissionControlDataFlowNode, useMissionControlDataFlowData, usePipelineNode, usePipelineNodeRelationships, usePipelineNodes } from "@stores/data.store";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from 'styled-components';
import PipelineNodeName from "./PipelineNodeName.component";
import { getImmediateDownstreamNodes, getImmediateUpstreamNodes } from "@components/missionControl/dataflow/utils";
import { PipelineNode, PipelineNodeORM } from "@models/pipelineNode";
import { Form, Dropdown as BSDropdown, Modal } from "react-bootstrap";
import toast from "@services/toast.service";
import { getErrorMessage } from "@services/errors.service";
import useGlobalState from "@stores/global.state";
import { getPromptAnswer, requireConfirmation, runBuild } from "@services/alert/alert.service";
import PipelineNodeSelector from "./PipelineNodeSelector.component";
import { useMeasures } from "@models/reportBuilder";
import { DraftOnly } from "@components/project/DraftModeRequired.component";
import { emit } from '@nextcloud/event-bus'
import ApiService from "@services/api/api.service";
import { useHotkeys } from "react-hotkeys-hook";
import BuildNodeModal from "./modal/BuildNodeModal.component";
import DAG from "@components/datamodel/Dag.component";
import { MissionControlNodeCard } from "@components/missionControl/dataflow/MissionControlDataFlow";
import NewCleaningStepModal from "./modal/NewCleaningStepModal.component";



const Section = styled.div`
margin-bottom: 1.5rem;
`
interface Props {
    pipelineNodeId: string;
}



const PipelineNodeHelper = (props: Props) => {

    const thisNode = usePipelineNode(props.pipelineNodeId);
    const dataFlowData = useMissionControlDataFlowData();

    const [cleaningNodes, setCleaningNodes] = useState<MissionControlDataFlowNode[]>([]);
    const [businessObjects, setBusinessObjects] = useState<MissionControlDataFlowNode[]>([]);
    const [reportNodes, setReportNodes] = useState<MissionControlDataFlowNode[]>([]);
    const [upstreamNodes, setUpstreamNodes] = useState<MissionControlDataFlowNode[]>([]);
    const [downstreamNodes, setDownstreamNodes] = useState<MissionControlDataFlowNode[]>([]);
    const [showNewCleaningStepModal, setShowNewCleaningStepModal] = useState(false);

    const allNodes = usePipelineNodes();

    const relationships = usePipelineNodeRelationships(props.pipelineNodeId);

    const sortNodes = useCallback((a: MissionControlDataFlowNode, b: MissionControlDataFlowNode) => {
        const arank = NODE_RANK[getGroupValueForNodeType(a.data.nodeType)];
        const brank = NODE_RANK[getGroupValueForNodeType(b.data.nodeType)];

        if (arank > brank) {
            return 1;
        } else if (brank > arank) {
            return -1;
        }

        return a.data.title > b.data.title ? 1: -1;
    }, []);

    const upstreamNodesAsObjects = useMemo(() => {
        if (!allNodes.data) {
            return [];
        }

        const nodesById: {[key: string]: PipelineNode} = {};
        allNodes.data.forEach(n => {
            nodesById[n.id as string] = n;
        });
        return upstreamNodes.map(n => nodesById[n.data.objectId]);
    }, [allNodes.dataUpdatedAt, upstreamNodes]);

    const downstreamBusinessObjects = useMemo(() => {
        if (!allNodes.data) {
            return [];
        }

        const nodesById: {[key: string]: PipelineNode} = {};
        allNodes.data.forEach(n => {
            nodesById[n.id as string] = n;
        });
        return businessObjects.map(n => nodesById[n.data.objectId]);
    }, [allNodes.dataUpdatedAt, businessObjects]);

    const downstreamReports = useMemo(() => {
        if (!allNodes.data) {
            return [];
        }

        const nodesById: {[key: string]: PipelineNode} = {};
        allNodes.data.forEach(n => {
            nodesById[n.id as string] = n;
        });
        return reportNodes.map(n => nodesById[n.data.objectId]);
    }, [allNodes.dataUpdatedAt, reportNodes]);

    const cleaningNodesAsObjects = useMemo(() => {
        if (!allNodes.data) {
            return [];
        }

        const nodesById: {[key: string]: PipelineNode} = {};
        allNodes.data.forEach(n => {
            nodesById[n.id as string] = n;
        });
        return cleaningNodes.map(n => nodesById[n.data.objectId]);
    }, [allNodes.dataUpdatedAt, cleaningNodes]);

    useEffect(() => {
        if (!dataFlowData.data) {
            setCleaningNodes([]);
            setBusinessObjects([]);
            setReportNodes([]);
        } else {
            const theNode = dataFlowData.data.nodes.find(n => n.data.objectId === props.pipelineNodeId);
            if (theNode) {
                
                setUpstreamNodes((getImmediateUpstreamNodes(theNode, dataFlowData.data.nodes, dataFlowData.data.edges, true)[0] as MissionControlDataFlowNode[]).sort(sortNodes));
                setDownstreamNodes((getImmediateDownstreamNodes(theNode, dataFlowData.data.nodes, dataFlowData.data.edges, true)[0] as MissionControlDataFlowNode[]).sort(sortNodes));

                // const [sourceNodes] = recursiveGetNodesAndEdges(theNode, preFilteredNodes, data.data.edges, [props.activeNodeId], [], 'source');

                // setDownstreamNodes(sourceNodes.sort(sortNodes));

                // const nodeIds = sourceNodes.map(n => n.id).concat(downstreamNodes.map(n => n.id));

                // setOtherNodes(preFilteredNodes.filter(n => !nodeIds.includes(n.id) && n.id != props.activeNodeId).sort(sortNodes));
            }
        }
    }, [dataFlowData.dataUpdatedAt, props.pipelineNodeId]);

    const relatedNodes = useMemo(() => {
        if (!relationships.data) {
            return [];
        }

        return relationships.data.map(r => {
            let otherNode: PipelineNode;
            if (r.child_node_id == props.pipelineNodeId) {
                otherNode = allNodes.data?.find(n => n.id == r.parent_node_id) as PipelineNode;
            } else {
                otherNode = allNodes.data?.find(n => n.id == r.child_node_id) as PipelineNode;
            }
            return otherNode;
        })
       
    }, [relationships.dataUpdatedAt, props.pipelineNodeId])

    const nodeGroupType = useMemo(() => {
        if (!thisNode.data) {
            return '';
        }
        return getGroupValueForNodeType(thisNode.data.node_type);
    }, [thisNode.dataUpdatedAt]);

    const { pageDirty } = useGlobalState();


    const checkFactory = useCallback(async () => {
        await ApiService.getInstance().request('GET', `/pipelinenodes/${props.pipelineNodeId}/factory`);
    
    }, [props.pipelineNodeId]);

    const [building, setBuilding] = useState(false);


    const triggerBuild = useCallback(async (selector: 'THIS' | 'UPSTREAM' | 'DOWNSTREAM' | 'ALL', force: boolean = false, check_for_records: boolean = false) => {
        if (!thisNode.data) {
            return;
        }
        

        if (pageDirty) {
            const confirmed = await requireConfirmation('You have unsaved changes. Are you sure you want to build this node?', 'Rebuild Node', 'Continue Building', 'Go Back');
            if (!confirmed) {
                return;
            }
        }

        setBuilding(true);
        try {
            let selectorValue: string = '';
            switch (selector) {
                case 'THIS':
                    selectorValue = thisNode.data.name;
                    break;
                case 'UPSTREAM':
                    selectorValue = '+' + thisNode.data.name;
                    break;
                case 'DOWNSTREAM':
                    selectorValue = thisNode.data.name + '+';
                    break;
                case 'ALL':
                    selectorValue = '+' + thisNode.data.name + '+'
                    break;
            }
            await runBuild(
                selectorValue, 
                force,
                check_for_records,
                <>
                    <button onClick={() => {
                        navigate(`/node/${props.pipelineNodeId}`);
                        emit('pliable:alerts:closebuild', {});
                    }} className="btn btn-primary me-1">View Data</button>
                    <button onClick={() => {
                        emit('pliable:alerts:closebuild', {});
                    }} className="btn btn-light">Continue Editing</button>
                </>,
                <>
                    <button onClick={() => {
                        emit('pliable:alerts:closebuild', {});
                    }} className="btn btn-light">Continue Editing</button>
                </>
            );


        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        } finally {
            setBuilding(false);
        }
    }, [thisNode.dataUpdatedAt, pageDirty]);

    useHotkeys('meta+enter', () => {
        setShowBuildNodeModal(true);
    }, [triggerBuild]);

    const navigate = useNavigate();

    const copyNode = useCallback(async () => {
        const newLabel = await getPromptAnswer('Enter new name for the copied node', 'Copy Node', false, `${thisNode.data?.label} copy`);
        if (!newLabel) {
            return;
        }
        const newNode = await PipelineNodeORM.copyPipelineNode(props.pipelineNodeId, newLabel);
        navigate(`/node/${newNode.id}/config`);
    }, [props.pipelineNodeId, thisNode.dataUpdatedAt]);

    const { setPageDirty } = useGlobalState();

    const deleteNode = useCallback(async () => {
        const confirmed = await requireConfirmation(`Are you sure you want to delete this node? Type out the full node name ("${thisNode.data?.label}") to continue.`, 'Delete Node', 'Delete', 'Cancel', thisNode.data?.label);
        if (confirmed) {
            setPageDirty(false);
            await PipelineNodeORM.deleteById(props.pipelineNodeId);
            navigate('/');
            invalidateEverything();
            toast('success', 'Node deleted', 'Node deleted successfully.');
        }
    }, [props.pipelineNodeId, thisNode.dataUpdatedAt]);

    const [showAddToBizObjModal, setShowAddToBizObjModal] = useState(false);
    const [showBuildNodeModal, setShowBuildNodeModal] = useState(false);

    const [selectedBusinessObjectId, setSelectedBusinessObjectId] = useState('');
    const [newBusinessObjectName, setNewBusinessObjectName] = useState('');

    const [addingToBO, setAddingToBO] = useState(false);
    const addToBusinessObject = useCallback(async () => {
        setAddingToBO(true);
        try {

        
            if (selectedBusinessObjectId == 'NEW') {
                const newBusinessObject = await PipelineNodeORM.save({
                    id: null,
                    name: newBusinessObjectName,
                    label: newBusinessObjectName,
                    node_type: 'DIMENSION',
                    fields: [],
                    table_name: '',
                    description: '',
                    upstream_node_ids: [],
                });
                setShowAddToBizObjModal(false);
                invalidatePipelineNodes();
                navigate(`/wizard/mapping/${props.pipelineNodeId}?bo_context=${newBusinessObject.id}`);
            } else {
                navigate(`/wizard/mapping/${props.pipelineNodeId}?bo_context=${selectedBusinessObjectId}`);
            }
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        } finally {
            setAddingToBO(false);
        }
    }, [newBusinessObjectName, selectedBusinessObjectId, props.pipelineNodeId]);

    const beginBuild = useCallback(async () => {
        setShowBuildNodeModal(true);
    }, [props.pipelineNodeId]);

    const measures = useMeasures();

    const [showDag, setShowDag] = useState(false);
    const [dagMode, setDagMode] = useState<'UPSTREAM' | 'DOWNSTREAM' | 'ALL'>('UPSTREAM');

    const sourceMeasures = useMemo(() => {
        if (!measures.data || !thisNode.data) {
            return [];
        }


        return measures.data.filter(m => (thisNode.data?.measure_ids || []).includes(m.id as string));
    }, [thisNode.dataUpdatedAt])

    return <>
        <DAG
            show={showDag}
            mode={dagMode}
            rootNodeId={'PipelineNode:' + props.pipelineNodeId}
            onHide={() => setShowDag(false)}
            nodeTitle={thisNode.data?.label}
            excludeDescendentBusinessObjects={true}
        />
        <BuildNodeModal 
            show={showBuildNodeModal} 
            onClose={() => setShowBuildNodeModal(false)}
            defaultCheck={thisNode.data?.node_type == 'SOURCE'}
            defaultUpstream={thisNode.data?.node_type != 'SOURCE'}
            header={`Build: ${thisNode.data?.label || ''}`}
            onConfirm={(selector: "THIS" | "UPSTREAM" | "DOWNSTREAM" | "ALL", force: boolean, check_for_records: boolean) => {triggerBuild(selector, force, check_for_records)}}
            />
        <Modal show={showAddToBizObjModal} onHide={() => setShowAddToBizObjModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Add to Entity</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label>Select an Entity</Form.Label>
                    <PipelineNodeSelector
                        selectedId={selectedBusinessObjectId}
                        onSelect={(pn) => setSelectedBusinessObjectId(pn?.id as string)}
                        optionFilter={(pn) => pn.node_type == 'DIMENSION'}
                        allowCreateNew
                    />

                </Form.Group>
                {selectedBusinessObjectId == 'NEW' && <>
                    <Form.Group>
                        <Form.Label>Enter Object Name</Form.Label>
                        <Form.Control type="text" value={newBusinessObjectName} onChange={(e) => setNewBusinessObjectName(e.target.value)}/>
                    </Form.Group>
                </>}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-light me-1" onClick={() => setShowAddToBizObjModal(false)}>Close</button>
                <button className="btn btn-success" onClick={addToBusinessObject} disabled={addingToBO || !selectedBusinessObjectId || (selectedBusinessObjectId == 'NEW' && !newBusinessObjectName)}>
                    {addingToBO ? 'Adding...' : 'Add'}
                </button>
            </Modal.Footer>
        </Modal>
        <NewCleaningStepModal
            show={showNewCleaningStepModal}
            onHide={() => setShowNewCleaningStepModal(false)}
            selectedNodeToClean={props.pipelineNodeId}
        />
        
        <div className="p-3">
           <h2>Quick Nav</h2>
            {/* <div className="d-flex center-vertically mb-2">
                <h3 className="mb-0 flex-1">Connections</h3>
                <Link className="btn btn-xs btn-rounded btn-primary" to={`/dag?focusNodeId=PipelineNode:${props.pipelineNodeId}`}>
                <i className="mdi mdi-sitemap mdi-rotate-270"></i> All
                </Link>
                
                </div> */}
            <div className="mb-3">
                <div className="d-flex center-vertically mb-2">
                    <div className="flex-1">
                        <h3 className="mb-0">Upstream</h3>
                        <div className="font-13 text-muted">Where does this data come from?</div>
                    </div>                    
                    <button className="btn btn-light" onClick={() => {
                        setShowDag(true);
                        setDagMode('UPSTREAM');
                    }}>
                        <i className="mdi mdi-sitemap mdi-rotate-90"></i>
                    </button>
                    
                </div>
                {upstreamNodes.map(us => {
                    return <div><MissionControlNodeCard
                        key={us.data.objectId}
                        data={us.data}
                        navigateOnClick
                        compact
                        className="mb-1"
                        onNavigate={() => {
                            // Close the DAG diagram if it's open
                            if (showDag) {
                                setShowDag(false);
                            }
                        }}
                    /></div>
                })}

                {thisNode.data?.node_type == 'DIMENSION' && <>
                    <div className="mt-2">
                        <Link className="btn btn-light btn-sm w-100" to={`/wizard/data-source?boContext=${props.pipelineNodeId}`}>
                        <i className="mdi mdi-connection"></i> Connect a data source
                        </Link>
                    </div>
                </>}
            </div>
            <div className="mb-3">
                <div className="d-flex center-vertically mb-2">
                    <div className="flex-1">
                        <h3 className="mb-0">Downstream</h3>
                        <div className="font-13 text-muted">Where does this data go?</div>
                    </div>
                    <button className="btn btn-light" onClick={() => {
                        setShowDag(true);
                        setDagMode('DOWNSTREAM');
                    }}>
                        <i className="mdi mdi-sitemap mdi-rotate-270"></i>
                    </button>
                    
                </div>
                {downstreamNodes.map(ds => {
                    return <div><MissionControlNodeCard
                        key={ds.data.objectId}
                        data={ds.data}
                        navigateOnClick
                        compact
                        className="mb-1"
                    /></div>
                })}
                 {['SOURCE', 'STAGING'].includes(nodeGroupType) && (
                    <button className="btn btn-light btn-sm mt-2 w-100" onClick={() => setShowNewCleaningStepModal(true)}>
                        <i className="mdi mdi-broom"></i> Clean from here
                    </button>
                
                )}
                {nodeGroupType === 'STAGING' && (
                    <button className="btn btn-light btn-sm mt-1 w-100" onClick={() => setShowAddToBizObjModal(true)}>
                        <i className="mdi mdi-map-marker-plus"></i> Map this to an Entity
                    </button>
                
                )}
            </div>
            
            {['DATA_MODELING', 'STAGING'].includes(nodeGroupType) && <div>
                <div className="mb-3">
                    <h3>Relationships ({relationships.data?.length})</h3>
                    
                    <div className="list-group">
                        {relationships.data && relationships.data
                            .filter(r => r.parent_node_id && r.child_node_id) // Add this line to filter relationships
                            .map(r => {
                                return (
                                    <div className="list-group-item list-group-item-action d-flex center-vertically" onClick={() => {
                                        navigate(`/relationship/${r.id}?afterSave=/node/${props.pipelineNodeId}/config`);
                                    }}>
                                        <div className="me-1">
                                            <i className={`mdi mdi-relation-${r.child_node_id == props.pipelineNodeId ? 'many-to-one' : 'one-to-many'} font-24`}></i>
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-13 text-muted">
                                                {r.child_node_id == props.pipelineNodeId ? 'Many-to-One' : 'One-to-Many'}
                                            </div>
                                            <div>
                                                <span className="fw-bold me-1"><PipelineNodeName pipelineNodeId={r.child_node_id == props.pipelineNodeId ? r.parent_node_id : r.child_node_id}/></span>
                                                <span>({r.name || 'No Name'})</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                    <DraftOnly>
                        <Link className="btn btn-light btn-sm w-100 mt-2" to={`/relationship/new?pipelineNodeId=${props.pipelineNodeId}&afterSave=/node/${props.pipelineNodeId}/config`}>
                        <i className="mdi mdi-plus-thick"></i> Create relationship
                        </Link>
                    </DraftOnly>
                </div>
            </div>}
            
            



        </div>
    </>
};

export default PipelineNodeHelper;